import type { ReactElement } from 'react';
import type { PlanDto } from '@gr/core/accounts/plans/dto';
import type { PlanLimit } from '@gr/core/accounts/plans/limits';
import { useT } from '../../../../app/di';
import { formatFileSize, formatNumber } from '../../../../shared/format';
import { Badge } from '../../../../ui-kit/Badge/Badge';
import { Button } from '../../../../ui-kit/Button/Button';
import { DataTable } from '../../../../ui-kit/DataTable/DataTable';
import { EmptyState } from '../../../../ui-kit/EmptyState/EmptyState';
import { amountBytes, limitedOnly } from '../model/plan-usage';
import styles from './PlanTable.module.scss';

export interface PlanTableProps {
  plans: readonly PlanDto[];
  pending: boolean;
  /** DEMO-RULE-3: демо-режим — без действий. */
  readOnly?: boolean;
  onEdit(plan: PlanDto): void;
  onMakeDefault(plan: PlanDto): void;
  onToggleArchive(plan: PlanDto): void;
}

/** PLAN-UC-1…3: планы с лимитами, отметками и числом пользователей. */
export function PlanTable({ plans, pending, readOnly = false, onEdit, onMakeDefault, onToggleArchive }: PlanTableProps): ReactElement {
  const { t, locale } = useT();
  const amount = (limit: PlanLimit, value: number): string => {
    const bytes = amountBytes(limit, value);
    return bytes === null ? formatNumber(value, locale) : formatFileSize(bytes, locale);
  };
  return (
    <DataTable
      label={t('plans.title')}
      rows={plans}
      rowKey={(plan) => plan.id}
      empty={<EmptyState title={t('plans.empty')} />}
      columns={[
        {
          key: 'plan',
          header: t('plans.plan'),
          primary: true,
          cell: (plan) => (
            <div className={styles.plan}>
              <strong className={styles.name}>{plan.name}</strong>
              <code className={styles.code}>{plan.code}</code>
              <div className="u-row">
                {plan.isDefault ? <Badge tone="accent">{t('plans.isDefault')}</Badge> : null}
                {plan.archived ? <Badge tone="warning">{t('plans.archived')}</Badge> : null}
              </div>
            </div>
          ),
        },
        {
          key: 'limits',
          header: t('plans.limitsColumn'),
          cell: (plan) => {
            const limited = limitedOnly(plan);
            return limited.length === 0 ? (
              <span className="u-muted">{t('plans.allUnlimited')}</span>
            ) : (
              <ul className={styles.limits}>
                {limited.map(({ limit, max }) => (
                  <li key={limit}>
                    <span className="u-muted">{t(`plans.usageLabels.${limit}`)}:</span> {amount(limit, max)}
                  </li>
                ))}
              </ul>
            );
          },
        },
        { key: 'users', header: t('plans.users'), align: 'end', cell: (plan) => formatNumber(plan.usersCount, locale) },
        {
          key: 'actions',
          header: t('plans.actions'),
          actions: true,
          cell: (plan) =>
            readOnly ? null : (
              <div className="u-row">
                <Button size="sm" icon="edit" disabled={pending} onClick={() => onEdit(plan)}>
                  {t('plans.edit')}
                </Button>
                {!plan.isDefault && !plan.archived ? (
                  <Button size="sm" variant="ghost" disabled={pending} onClick={() => onMakeDefault(plan)}>
                    {t('plans.makeDefault')}
                  </Button>
                ) : null}
                {!plan.isDefault ? (
                  <Button size="sm" variant="ghost" disabled={pending} onClick={() => onToggleArchive(plan)}>
                    {plan.archived ? t('plans.unarchive') : t('plans.archive')}
                  </Button>
                ) : null}
              </div>
            ),
        },
      ]}
    />
  );
}
